const LOCALE = 'en-US';
const MAX_FRACTION_DIGITS = 2;

type FormatNumberToDecimalOptions = {
  /**
   * The number of decimal places to include in the formatted number.
   * Default is 0.
   */
  decimals: number;

  /**
   * The maximum value above which the number should not be formatted with decimals.
   * Default is 0.
   */
  upperLimit?: number;
};

/**
 * Formats a number to a string with a specified number of decimal places.
 * Optionally, upper limit can be set above which the number should not be formatted with decimals.
 * @param value The number to format.
 * @param options The options to use when formatting the number.
 * @returns The formatted number as a string.
 * @example
 * formatNumberToDecimal(1234.5678, { decimals: 2 }); // '1234.57'
 * formatNumberToDecimal(1234.5678, { decimals: 2, upperLimit: 1000 }); // '1234'
 */
export function formatNumberToDecimal(
  value: number,
  options?: FormatNumberToDecimalOptions
): string {
  const { upperLimit = Infinity, decimals = 0 } = options || {};

  if (isNaN(value)) {
    return '';
  }

  const decimalPlaces = value >= upperLimit ? 0 : decimals;
  return value.toFixed(decimalPlaces);
}

/**
 * Formats a number to a string with commas as thousand separators.
 * Optionally, the number can be formatted with units (K, M, B).
 * @param num The number to format.
 * @param withUnits Whether to format the number with units (K, M, B).
 * @returns The formatted number as a string.
 * @example
 * formatNumericValue(1234); // '1,234'
 * formatNumericValue(1234, true); // '1.23K'
 */
export const formatNumericValue = (num: number, withUnits = false): string => {
  if (!withUnits) {
    return num.toLocaleString(LOCALE);
  }

  let formattedNumber: string;
  const absNum = Math.abs(num);

  if (absNum >= 1_000_000_000) {
    formattedNumber =
      (num / 1_000_000_000).toLocaleString(LOCALE, { maximumFractionDigits: MAX_FRACTION_DIGITS }) +
      'B';
  } else if (absNum >= 1_000_000) {
    formattedNumber =
      (num / 1_000_000).toLocaleString(LOCALE, { maximumFractionDigits: MAX_FRACTION_DIGITS }) +
      'M';
  } else if (absNum >= 1_000) {
    formattedNumber =
      (num / 1_000).toLocaleString(LOCALE, { maximumFractionDigits: MAX_FRACTION_DIGITS }) + 'K';
  } else {
    formattedNumber = num.toLocaleString(LOCALE);
  }

  return formattedNumber;
};

export const numberToOrdinal = (num: number, locale?: string): string => {
  const enOrdinalRules = new Intl.PluralRules(locale || 'en-US', { type: 'ordinal' });

  const suffixes = new Map([
    ['one', 'st'],
    ['two', 'nd'],
    ['few', 'rd'],
    ['other', 'th'],
  ]);

  const rule = enOrdinalRules.select(num);
  const suffix = suffixes.get(rule);
  return `${num}${suffix}`;
};
